import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Send, CheckCircle, ArrowRight, Calendar, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  FRONT_DESK_PHONE_DISPLAY,
  FRONT_DESK_HOURS,
  FRONT_DESK_LABEL,
  FRONT_DESK_TEL_HREF,
} from '../lib/siteContact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeIn = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-30px' },
  transition: { duration: 0.5 },
};

export const ContactSection = ({ calendlyLink, instagramLink }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim() || formData.name.trim().length < 2)
      newErrors.name = 'Please enter your name';
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email))
      newErrors.email = 'Please enter a valid email';
    if (!formData.role)
      newErrors.role = 'Please select your role';
    if (!formData.message.trim() || formData.message.trim().length < 10)
      newErrors.message = 'Tell us a bit more (at least 10 characters)';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.status === 429) { toast.error('Too many requests. Please try again shortly.'); return; }
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Something went wrong'); }
      setSubmitted(true);
      toast.success('Message sent!');
    } catch (err) {
      toast.error(err.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
  };

  return (
    <section
      id="contact"
      className="py-20 sm:py-24 bg-gradient-to-br from-[#0B0C0D] via-[#0f1516] to-[#0B0C0D] dark:from-[#0B0C0D] dark:via-[#0f1516] dark:to-[#0B0C0D] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(600px_circle_at_50%_30%,rgba(0,76,84,0.12),transparent)] dark:bg-[radial-gradient(600px_circle_at_50%_30%,rgba(0,76,84,0.12),transparent)]" />
      <div className="absolute inset-0 noise opacity-[0.02] dark:opacity-[0.02]" />
      
      <div className="relative max-w-[1120px] mx-auto px-5 sm:px-6">
        <motion.div {...fadeIn} className="text-center mb-8">
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#A5ACAF] dark:text-[#A5ACAF] font-semibold mb-2">
            Get Started
          </div>
          <h2 className="font-heading text-[1.65rem] sm:text-3xl font-bold tracking-tight text-white dark:text-white mb-2">
            Let's Talk About Your Goals
          </h2>
          <p className="text-[14px] sm:text-[15px] text-white/50 dark:text-white/50 max-w-[40ch] mx-auto">
            Send a message or book a call. A real conversation — to see if there's a current opening and if it's the right fit.
          </p>
          <p
            className="text-[12.5px] text-white/45 max-w-[52ch] mx-auto mt-3"
            data-testid="contact-service-area"
          >
            Based in Cherry Hill, NJ. Serving athletes, parents, teams, schools, and clubs across New Jersey and virtually.
          </p>
        </motion.div>

        <motion.div {...fadeIn}>
          <div className="grid lg:grid-cols-[1fr_340px] gap-5 lg:gap-6">
            {/* Form */}
            <div>
              {submitted ? (
                <Card className="border border-[#004C54]/20 bg-white/95 backdrop-blur-sm rounded-xl" data-testid="contact-form-success-state">
                  <CardContent className="p-8 sm:p-10 text-center">
                    <div className="w-12 h-12 rounded-full bg-[#004C54]/15 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="h-6 w-6 text-[#004C54]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground mb-1.5">Message Received</h3>
                    <p className="text-[14px] text-muted-foreground mb-5 max-w-[36ch] mx-auto">
                      I'll get back to you within 24–48 hours. Want to get started faster?
                    </p>
                    <Button asChild className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-11 px-5 text-sm rounded-[10px]">
                      <a href={calendlyLink} data-testid="success-book-call-button">
                        Book a Discovery Call <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-white/10 rounded-xl bg-white/95 backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
                  <CardContent className="p-5 sm:p-6">
                    <form onSubmit={handleSubmit} noValidate>
                      <div className="hidden" aria-hidden="true">
                        <input type="text" name="honeypot" tabIndex={-1} autoComplete="off" value={formData.honeypot} onChange={(e) => handleChange('honeypot', e.target.value)} />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label htmlFor="name" className="text-[13px] font-medium mb-1.5 block">Name <span className="text-destructive">*</span></Label>
                          <Input id="name" type="text" placeholder="Your full name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} className={`h-10 text-[13px] ${errors.name ? 'border-destructive' : ''}`} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-testid="contact-form-name-input" />
                          {errors.name && <p id="name-error" className="text-[11px] text-destructive mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-[13px] font-medium mb-1.5 block">Email <span className="text-destructive">*</span></Label>
                          <Input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className={`h-10 text-[13px] ${errors.email ? 'border-destructive' : ''}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-testid="contact-form-email-input" />
                          {errors.email && <p id="email-error" className="text-[11px] text-destructive mt-1">{errors.email}</p>}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label htmlFor="phone" className="text-[13px] font-medium mb-1.5 block">Phone <span className="text-muted-foreground text-[11px]">(optional)</span></Label>
                          <Input id="phone" type="tel" placeholder="862-693-0177" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} className="h-10 text-[13px]" data-testid="contact-form-phone-input" />
                        </div>
                        <div>
                          <Label className="text-[13px] font-medium mb-1.5 block">I am a... <span className="text-destructive">*</span></Label>
                          <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                            <SelectTrigger className={`h-10 text-[13px] ${errors.role ? 'border-destructive' : ''}`} aria-invalid={!!errors.role} data-testid="contact-form-role-select">
                              <SelectValue placeholder="Select your role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="athlete">Athlete</SelectItem>
                              <SelectItem value="parent">Parent of Athlete</SelectItem>
                              <SelectItem value="coach">Coach</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.role && <p className="text-[11px] text-destructive mt-1">{errors.role}</p>}
                        </div>
                      </div>

                      <div className="mb-5">
                        <Label htmlFor="message" className="text-[13px] font-medium mb-1.5 block">Message <span className="text-destructive">*</span></Label>
                        <Textarea id="message" placeholder="Tell me about your goals, challenges, or questions..." rows={4} value={formData.message} onChange={(e) => handleChange('message', e.target.value)} className={`resize-none text-[13px] ${errors.message ? 'border-destructive' : ''}`} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} data-testid="contact-form-message-textarea" />
                        {errors.message && <p id="message-error" className="text-[11px] text-destructive mt-1">{errors.message}</p>}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <Button type="submit" disabled={loading} className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold h-12 px-6 text-sm rounded-[10px] shadow-lg shadow-[#004C54]/20 active:scale-[0.98] transition-all duration-200 w-full sm:w-auto" data-testid="contact-form-submit-button">
                          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<><Send className="mr-2 h-4 w-4" />Send Message</>)}
                        </Button>
                        <p className="text-[11px] text-muted-foreground/70">Takes under 2 minutes. No spam, ever.</p>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-3.5">
              {/* Phase 35 — Call Front Desk card. Placed FIRST so prospects
                  who want a human voice can opt for it before scrolling to
                  the calendar / Instagram tile. */}
              <Card
                className="border border-[#4FBCC7]/30 bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden"
                data-testid="contact-call-card"
              >
                <div className="bg-gradient-to-br from-[#004C54] to-[#003a40] px-5 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Phone className="h-4 w-4 text-[#4FBCC7]" />
                    <span className="font-mono-accent text-[9px] uppercase tracking-[0.15em] text-white/55">
                      Prefer to call?
                    </span>
                  </div>
                  <h3 className="font-heading text-base font-bold text-white">
                    {FRONT_DESK_LABEL}
                  </h3>
                </div>
                <CardContent className="p-5">
                  <a
                    href={FRONT_DESK_TEL_HREF}
                    className="block font-heading text-[1.55rem] sm:text-[1.7rem] font-bold tracking-tight text-[#004C54] hover:text-[#003a40] transition-colors"
                    data-testid="contact-call-number"
                  >
                    {FRONT_DESK_PHONE_DISPLAY}
                  </a>
                  <p className="text-[12.5px] text-muted-foreground mt-1">
                    {FRONT_DESK_HOURS}
                  </p>
                  <p className="text-[12px] text-muted-foreground/85 mt-2.5 leading-snug">
                    Talk to our front desk. We&rsquo;ll route your question to Coach Lewis or get a Discovery Call on the calendar.
                  </p>
                  <Button
                    asChild
                    className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold w-full h-11 text-[13px] mt-3.5 active:scale-[0.98]"
                  >
                    <a href={FRONT_DESK_TEL_HREF} data-testid="contact-call-button">
                      <Phone className="h-3.5 w-3.5 mr-1.5" />
                      Call Now
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-[#004C54]/20 bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden" data-testid="calendly-placeholder-card">
                <div className="bg-gradient-to-br from-[#004C54] to-[#003a40] px-5 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-white/60" />
                    <span className="font-mono-accent text-[9px] uppercase tracking-[0.15em] text-white/50">Prefer to talk?</span>
                  </div>
                  <h3 className="font-heading text-base font-bold text-white">Book a Discovery Call</h3>
                </div>
                <CardContent className="p-5">
                  <p className="text-[14px] text-muted-foreground mb-3.5">30 minutes. By design, limited availability. A mutual fit check.</p>
                  <Button asChild className="bg-[#0B0C0D] hover:bg-[#1a1d1f] text-white font-semibold w-full h-11 text-[13px] active:scale-[0.98]">
                    <a href={calendlyLink} data-testid="sidebar-book-call-button">Book Now <ArrowRight className="ml-2 h-3.5 w-3.5" /></a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-white/10 rounded-xl bg-white/95 backdrop-blur-sm" data-testid="instagram-card">
                <CardContent className="p-5">
                  <h3 className="font-heading text-[13px] font-semibold text-foreground mb-1">Follow on Instagram</h3>
                  <p className="text-[12px] text-muted-foreground mb-1.5">Tips, athlete stories, and mental performance content.</p>
                  <a href={instagramLink} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-[#004C54] hover:underline underline-offset-4" data-testid="instagram-placeholder-link">@apexathleticmind →</a>
                </CardContent>
              </Card>

              <Card className="border border-white/10 rounded-xl bg-white/95 backdrop-blur-sm" data-testid="direct-contact-card">
                <CardContent className="p-5">
                  <h3 className="font-heading text-[13px] font-semibold text-foreground mb-1.5">Direct Contact</h3>
                  <p className="text-[14px] text-muted-foreground"><a href="mailto:volewis@apexathleticmind.com" className="text-[#004C54] font-medium hover:underline" data-testid="direct-contact-email-link">volewis@apexathleticmind.com</a></p>
                  <p className="text-[14px] text-muted-foreground mt-0.5">
                    <a href="tel:+18626930177" className="text-[#004C54] font-medium hover:underline" data-testid="direct-contact-phone-link">862-693-0177</a>
                    <span className="text-[11px] text-muted-foreground/60 ml-1">(Direct)</span>
                  </p>
                  <p className="text-[14px] text-muted-foreground mt-0.5">
                    <a href="tel:+18626930177" className="text-[#004C54] font-medium hover:underline" data-testid="direct-contact-front-desk-link">862-693-0177</a>
                    <span className="text-[11px] text-muted-foreground/60 ml-1">(Front Desk)</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground/60 mt-1.5">Cherry Hill, NJ &middot; By appointment 3:00 PM &ndash; 8:30 PM ET</p>
                  <p className="text-[11px] text-muted-foreground/60 mt-0.5">Responses within 24&ndash;48 hours</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
